import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Leaf,
  Droplets,
  Settings,
  Sprout,
  Wrench,
  Headphones,
  GraduationCap,
  CheckCircle2,
} from "lucide-react";
import {
  ArrowLeft,
  ShieldCheck,
  BarChart3,
  Thermometer,
  Zap,
  Phone,
} from "lucide-react";
// =====================================================
// IMAGES
// Change these filenames according to your src/image/
// =====================================================

import HeroImage from "../image/solutionheropage.png";
import NFTImage from "../image/nft-system.png";
import DWCImage from "../image/dwc-system.png";
import VerticalImage from "../image/vertical-farming.png";
import GreenhouseImage from "../image/greenhouse-solutions.png";

import LettuceImage from "../image/lettuce_2.png";
import SpinachImage from "../image/spinach.png";
import BasilImage from "../image/basil.png";
import TomatoImage from "../image/tomato.png";
import CucumberImage from "../image/cucumber2.png";
import StrawberryImage from "../image/strawberry.png";
import CapsicumImage from "../image/capsicum.png";
import MintImage from "../image/mint.png";

// =====================================================
// SOLUTION DATA
// =====================================================

const solutions = [
  {
    slug: "nft-system",
    title: "NFT System",
    subtitle: "Nutrient Film Technique",
    image: NFTImage,
    description:
      "A popular and efficient system where a thin film of nutrient solution flows continuously over the roots.",
    features: [
      "Ideal for leafy greens",
      "High oxygen supply",
      "Efficient & scalable",
    ],

    overview:
      "The Nutrient Film Technique (NFT) system continuously circulates a thin film of nutrient-rich water through growing channels. Plant roots receive water, nutrients and oxygen while growing without traditional soil.",

    benefits: [
      {
        icon: <Leaf size={25} />,
        title: "Ideal for Leafy Greens",
        text: "Perfect for lettuce, spinach, herbs and other fast-growing crops.",
      },
      {
        icon: <Droplets size={25} />,
        title: "Efficient Water Use",
        text: "Nutrient solution is continuously circulated through the system.",
      },
      {
        icon: <Zap size={25} />,
        title: "High Oxygen Supply",
        text: "Roots receive excellent oxygen availability for healthy growth.",
      },
      {
        icon: <BarChart3 size={25} />,
        title: "Scalable System",
        text: "Suitable for small farms as well as larger commercial setups.",
      },
    ],
  },

  {
    slug: "dwc-system",
    title: "DWC System",
    subtitle: "Deep Water Culture",
    image: DWCImage,
    description:
      "Plant roots are suspended in nutrient-rich water with continuous aeration for healthy growth.",
    features: [
      "Simple & cost-effective",
      "Great for leafy vegetables",
      "Low maintenance",
    ],

    overview:
      "Deep Water Culture is a simple hydroponic system where plant roots remain suspended in nutrient-rich water. Air pumps provide continuous oxygen to the root zone, supporting healthy plant development.",

    benefits: [
      {
        icon: <Droplets size={25} />,
        title: "Nutrient-Rich Water",
        text: "Plants receive nutrients directly through the water surrounding their roots.",
      },
      {
        icon: <Settings size={25} />,
        title: "Simple Setup",
        text: "A straightforward hydroponic system that is easy to understand and maintain.",
      },
      {
        icon: <Leaf size={25} />,
        title: "Great for Leafy Crops",
        text: "Well suited for lettuce, spinach and other leafy vegetables.",
      },
      {
        icon: <ShieldCheck size={25} />,
        title: "Low Maintenance",
        text: "Simple components make routine monitoring and maintenance easier.",
      },
    ],
  },

  {
    slug: "vertical-farming",
    title: "Vertical Farming",
    subtitle: "Space-Saving Solution",
    image: VerticalImage,
    description:
      "Grow more in less space with vertical hydroponic systems, perfect for urban and indoor farming.",
    features: [
      "Ideal for urban areas",
      "High yield per square foot",
      "Automated options available",
    ],

    overview:
      "Vertical farming uses stacked growing levels to maximize available space. Combined with hydroponic technology, it allows growers to produce crops efficiently in urban, indoor and space-constrained environments.",

    benefits: [
      {
        icon: <Sprout size={25} />,
        title: "Space Efficient",
        text: "Multiple growing levels allow more production within a smaller footprint.",
      },
      {
        icon: <BarChart3 size={25} />,
        title: "Higher Productivity",
        text: "Maximize production potential by utilizing vertical growing space.",
      },
      {
        icon: <Settings size={25} />,
        title: "Automation Ready",
        text: "Systems can be integrated with monitoring and automated controls.",
      },
      {
        icon: <Leaf size={25} />,
        title: "Urban Farming",
        text: "Suitable for cities, indoor farms and areas with limited land.",
      },
    ],
  },

  {
    slug: "greenhouse-solutions",
    title: "Greenhouse Solutions",
    subtitle: "Controlled Environment",
    image: GreenhouseImage,
    description:
      "Custom greenhouse setups with climate control for consistent year-round production.",
    features: [
      "Weather independent",
      "Higher productivity",
      "Suitable for commercial farms",
    ],

    overview:
      "Our greenhouse solutions provide a controlled growing environment where temperature, humidity, irrigation and other growing conditions can be managed according to crop requirements.",

    benefits: [
      {
        icon: <Thermometer size={25} />,
        title: "Climate Control",
        text: "Manage temperature and other environmental conditions for crop growth.",
      },
      {
        icon: <ShieldCheck size={25} />,
        title: "Weather Protection",
        text: "Protect crops from changing outdoor weather conditions.",
      },
      {
        icon: <BarChart3 size={25} />,
        title: "Higher Productivity",
        text: "Controlled growing conditions can support consistent production.",
      },
      {
        icon: <Sprout size={25} />,
        title: "Commercial Farming",
        text: "Suitable for commercial farms and larger controlled-environment projects.",
      },
    ],
  },
];

// =====================================================
// CROPS
// =====================================================

const crops = [
  {
    name: "Lettuce",
    image: LettuceImage,
  },
  {
    name: "Spinach",
    image: SpinachImage,
  },
  {
    name: "Basil",
    image: BasilImage,
  },
  {
    name: "Tomato",
    image: TomatoImage,
  },
  {
    name: "Cucumber",
    image: CucumberImage,
  },
  {
    name: "Strawberry",
    image: StrawberryImage,
  },
  {
    name: "Capsicum",
    image: CapsicumImage,
  },
  {
    name: "Mint",
    image: MintImage,
  },
];

// =====================================================
// COMPONENT
// =====================================================

export default function OurSolutions() {
  return (
    <div className="min-h-screen bg-white text-[#10231c]">
      {/* =================================================
          HERO
      ================================================= */}
      <section className="relative overflow-hidden">
        {/* Hero Image */}
        <div className="relative min-h-[520px] lg:min-h-[540px]">
          <img
            src={HeroImage}
            alt="Hydroponic farming solutions"
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* White gradient on left */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/10" />

          {/* Additional soft green overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#f7fbf7]/80 via-transparent to-transparent" />

          {/* Hero Content */}
          <div className="relative z-10 mx-auto flex min-h-[520px] max-w-[1400px] items-center px-5 py-20 lg:min-h-[540px] lg:px-10">
            <div className="max-w-[530px]">
              {/* Breadcrumb */}
              <div className="mb-7 flex items-center gap-2 text-sm font-medium text-gray-600">
                <Link to="/" className="transition hover:text-[#087f4f]">
                  Home
                </Link>

                <span>›</span>

                <span className="text-[#087f4f]">Solutions</span>
              </div>

              {/* Small Heading */}
              <p className="mb-2 text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                Innovative Hydroponic Solutions
              </p>

              {/* Heading */}
              <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-[52px]">
                Better Farming
                <br />
                Starts with
                <br />
                <span className="text-[#087f4f]">Smarter Solutions</span>
              </h1>

              {/* Description */}
              <p className="mt-5 max-w-[470px] text-base leading-6 text-gray-600">
                At Polybionic Technologies, we design and deliver advanced
                hydroponic farming solutions tailored for modern agriculture.
                From compact systems to large-scale commercial setups, we help
                you grow more with less.
              </p>

              {/* Buttons */}
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#066b42]"
                >
                  Talk to Our Experts
                  <ArrowRight size={16} />
                </Link>

                <a
                  href="#solutions"
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-bold text-gray-800 shadow-sm transition hover:border-[#087f4f] hover:text-[#087f4f]"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#087f4f] text-[9px] text-white">
                    ▶
                  </span>
                  Watch How It Works
                </a>
              </div>
            </div>

            {/* Right Green Panel */}
            <div className="absolute bottom-0 right-0 hidden h-[235px] w-[285px] overflow-hidden rounded-tl-[120px] bg-[#004f35] lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-[#006341] to-[#003b29]" />

              <div className="relative flex h-full flex-col items-center justify-center px-8 text-center">
                <p
                  className="text-3xl font-semibold italic leading-tight text-white"
                  style={{
                    fontFamily: "Georgia, serif",
                  }}
                >
                  Good Food
                  <br />
                  Brighter Tomorrow
                </p>

                <div className="mt-4 h-[3px] w-40 rotate-[-5deg] rounded-full bg-[#69c92e]" />

                <div className="mt-5 flex items-center gap-2 text-white">
                  <Leaf size={28} />

                  <div className="text-left text-xs font-bold leading-5">
                    <p>Sustainable</p>
                    <p>Scalable</p>
                    <p>Profitable</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================================================
          BENEFITS
      ================================================= */}
      <section className="border-y border-green-100 bg-[#f5fbf7]">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 px-5 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
          {/* Benefit 1 */}
          <div className="flex items-center gap-4 border-b border-green-100 px-4 py-5 sm:border-r lg:border-b-0">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
              <Leaf size={26} />
            </div>

            <div>
              <h3 className="text-sm font-bold">Higher Yield</h3>

              <p className="text-xs text-gray-500">Grow more in less space</p>
            </div>
          </div>

          {/* Benefit 2 */}
          <div className="flex items-center gap-4 border-b border-green-100 px-4 py-5 lg:border-b-0 lg:border-r">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
              <Droplets size={26} />
            </div>

            <div>
              <h3 className="text-sm font-bold">Uses 90% Less Water</h3>

              <p className="text-xs text-gray-500">Conserve resources</p>
            </div>
          </div>

          {/* Benefit 3 */}
          <div className="flex items-center gap-4 border-b border-green-100 px-4 py-5 sm:border-r lg:border-b-0">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
              <Settings size={26} />
            </div>

            <div>
              <h3 className="text-sm font-bold">Automated Systems</h3>

              <p className="text-xs text-gray-500">Save time and effort</p>
            </div>
          </div>

          {/* Benefit 4 */}
          <div className="flex items-center gap-4 px-4 py-5">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
              <Sprout size={26} />
            </div>

            <div>
              <h3 className="text-sm font-bold">Year-Round Farming</h3>

              <p className="text-xs text-gray-500">Consistent fresh produce</p>
            </div>
          </div>
        </div>
      </section>

      {/* =================================================
          COMPLETE HYDROPONIC SOLUTIONS
      ================================================= */}
      <section id="solutions" className="bg-white py-16 lg:py-20 scroll-right">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          {/* Section Heading */}
          <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
            <div>
              <p className="mb-2 text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                Our Solutions
              </p>

              <h2 className="text-3xl font-extrabold sm:text-4xl">
                Complete Hydroponic Solutions
              </h2>

              <p className="mt-3 max-w-[680px] text-sm leading-6 text-gray-600">
                From design to deployment, we provide end-to-end hydroponic
                systems for homes, businesses and commercial farms. Choose the
                right solution for your goals.
              </p>
            </div>

            {/* Decorative Green Leaves */}
            <div className="hidden items-center gap-3 text-[#087f4f] md:flex">
              <Leaf size={46} />

              <p className="text-sm font-bold leading-5">
                Innovative Systems
                <br />
                for a Greener Planet
              </p>
            </div>
          </div>

          {/* Cards */}
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="group overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                {/* Image */}
                <div className="h-[170px] overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-lg font-bold">{solution.title}</h3>

                  <p className="mt-1 text-xs font-medium text-gray-500">
                    {solution.subtitle}
                  </p>

                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    {solution.description}
                  </p>

                  {/* Features */}
                  <div className="mt-4 space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-start gap-2 text-xs text-gray-600"
                      >
                        <CheckCircle2
                          size={15}
                          className="mt-[1px] flex-shrink-0 text-[#19a35c]"
                        />

                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Learn More */}
                  <Link
                    to={`/solutions/${solution.slug}`}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-[#087f4f] transition hover:gap-2"
                  >
                    Learn More
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================
          CUSTOM SOLUTIONS
      ================================================= */}
      <section className="bg-[#eef9f1] py-10 lg:py-12 scroll-left">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          {/* Heading + Button */}
          <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div>
              <p className="mb-1 text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                Custom Solutions
              </p>

              <h2 className="text-3xl font-extrabold">
                Tailored Systems for Your Needs
              </h2>

              <p className="mt-2 max-w-[700px] text-sm leading-6 text-gray-600">
                Every farm is unique. We offer customized hydroponic solutions
                based on your space, crop type and business goals.
              </p>
            </div>

            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#066b42]"
            >
              Get a Custom Solution
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Services */}
          <div className="mt-7 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {/* Design */}
            <div className="flex items-center gap-4 border-b border-green-200 pb-5 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-5">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f]">
                <Wrench size={24} />
              </div>

              <div>
                <h3 className="text-sm font-bold">Design & Consultation</h3>

                <p className="mt-1 text-xs text-gray-500">
                  Site analysis and planning
                </p>
              </div>
            </div>

            {/* Installation */}
            <div className="flex items-center gap-4 border-b border-green-200 pb-5 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-5">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f]">
                <Settings size={24} />
              </div>

              <div>
                <h3 className="text-sm font-bold">System Installation</h3>

                <p className="mt-1 text-xs text-gray-500">Professional setup</p>
              </div>
            </div>

            {/* Training */}
            <div className="flex items-center gap-4 border-b border-green-200 pb-5 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-5">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f]">
                <GraduationCap size={24} />
              </div>

              <div>
                <h3 className="text-sm font-bold">Training & Support</h3>

                <p className="mt-1 text-xs text-gray-500">
                  Learn and grow confidently
                </p>
              </div>
            </div>

            {/* Support */}
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f]">
                <Headphones size={24} />
              </div>

              <div>
                <h3 className="text-sm font-bold">After-Sales Service</h3>

                <p className="mt-1 text-xs text-gray-500">
                  We're always here to help
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================================================
          IDEAL CROPS
      ================================================= */}
      <section className="bg-white py-12 lg:py-14 scroll-right">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          {/* Heading */}
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="mb-1 text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                Ideal Crops
              </p>

              <h2 className="text-3xl font-extrabold">
                Grow a Variety of Crops
              </h2>

              <p className="mt-2 text-sm text-gray-600">
                Our hydroponic systems support a wide range of high-value crops.
              </p>
            </div>

            <Link
              to="/products"
              className="inline-flex w-fit items-center gap-2 rounded-full border border-[#087f4f] px-5 py-2.5 text-sm font-bold text-[#087f4f] transition hover:bg-[#087f4f] hover:text-white"
            >
              View All Crops
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Crop Cards */}
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
            {crops.map((crop, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              >
                <div className="h-[90px] overflow-hidden bg-gray-50">
                  <img
                    src={crop.image}
                    alt={crop.name}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="px-2 py-2.5 text-center">
                  <p className="text-xs font-bold text-gray-700">{crop.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================
          CTA
      ================================================= */}
      <section className="relative overflow-hidden bg-[#005535]">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#004b31] to-[#087f4f]" />

        <div className="relative mx-auto flex max-w-[1400px] flex-col justify-between gap-6 px-5 py-7 sm:flex-row sm:items-center lg:px-10">
          <div className="flex items-center gap-4">
            <div className="hidden h-12 w-12 items-center justify-center rounded-full bg-white/10 text-[#73d35c] sm:flex">
              <Leaf size={30} />
            </div>

            <div>
              <h2 className="text-2xl font-extrabold text-white">
                Ready to Build Your Hydroponic Farm?
              </h2>

              <p className="mt-1 text-sm text-white/80">
                Let's discuss the right solution for your goals. Get expert
                guidance from our team.
              </p>
            </div>
          </div>

          <Link
            to="/contact"
            className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-bold text-[#087f4f] transition hover:bg-green-50"
          >
            Get a Quote
            <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </div>
  );
}
